import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './Button';
import { CorporateLogo } from './CorporateLogo';

import {
  MenuOutlined,
  TeamOutlined,
  CalculatorOutlined,
  BarChartOutlined,
  FileAddOutlined,
  ReconciliationTwoTone,
  AppstoreTwoTone,
} from '@ant-design/icons';
import { Drawer } from 'antd';
import { useUserContext } from '../context/PermissionsContext';
import Historybreadcrumb from './generic/Breadcrumb/Historybreadcrumb';
import useCyberFlag from '../hooks/cyber/useCyberFlag';

function SideMenu() {
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin } = useUserContext();
  const location = useLocation();

  const { isEnabled } = useCyberFlag();

  const [visible, setVisible] = useState(false);

  const shouldBeHighlighted = (path: string) => {
    return window.location.pathname === path;
  };

  const showDrawer = () => {
    setVisible(true);
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  const handleMenuClick = (path: string) => {
    navigate(path);
    if (visible) closeDrawer();
  };

  const menuContent = (
    <div className="flex flex-col justify-between h-full">
      <div>
        <CorporateLogo />
        <h2 className="w-full mt-10 text-xsm font-semibold text-grey tracking-wider">
          DASHBOARDS & REPORTS
        </h2>
        <Button
          type="menu"
          className={`group rounded-md py-2 px-3 sidebar-icon ${
            shouldBeHighlighted('/risk-hub') ? 'sidebar-icon--active' : ''
          }`}
          onClick={() => handleMenuClick('/risk-hub')}
        >
          <div className="w-full flex items-center gap-3">
            <BarChartOutlined
              style={{ fontSize: '20px', color: '#437EF7' }}
              className="transition-transform duration-300 group-hover:scale-125"
            />
            Risk Hub
          </div>
        </Button>
        <Button
          type="menu"
          className={`group rounded-md py-2 px-3 sidebar-icon ${
            shouldBeHighlighted('/bcp-hub') ? 'sidebar-icon--active' : ''
          }`}
          onClick={() => handleMenuClick('/bcp-hub')}
        >
          <div className="w-full flex items-center gap-3">
            <ReconciliationTwoTone
              style={{ fontSize: '20px', color: '#437EF7' }}
              className="transition-transform duration-300 group-hover:scale-125"
            />
            BCP Hub
          </div>
        </Button>
        {isEnabled && (
        <Button
          type="menu"
          className={`group rounded-md py-2 px-3 sidebar-icon ${
            shouldBeHighlighted('/cyber-hub') ? 'sidebar-icon--active' : ''
          }`}
          onClick={() => handleMenuClick('/cyber-hub')}
        >
          <div className="w-full flex items-center gap-3">
            <AppstoreTwoTone
              style={{ fontSize: '20px', color: '#437EF7' }}
              className="transition-transform duration-300 group-hover:scale-125"
            />
              Cyber Hub
            </div>
          </Button>
        )}

        <h2 className="w-full mt-10 text-xsm font-semibold text-grey tracking-wider">
          RISK QUANTIFICATION
        </h2>
        <Button
          type="menu"
          className={`group rounded-md py-2 px-3 sidebar-icon ${
            shouldBeHighlighted('/data-inputs') ? 'sidebar-icon--active' : ''
          }`}
          onClick={() => handleMenuClick('/data-inputs')}
        >
          <div className="w-full flex items-center gap-3">
            <FileAddOutlined
              style={{ fontSize: '20px', color: '#437EF7' }}
              className="transition-transform duration-300 group-hover:scale-125"
            />
            Data Inputs
          </div>
        </Button>
        <Button
          type="menu"
          className={`group rounded-md py-2 px-3 sidebar-icon ${
            shouldBeHighlighted('/exposures') ? 'sidebar-icon--active' : ''
          }`}
          onClick={() => handleMenuClick('/exposures')}
        >
          <div className="w-full flex items-center gap-3">
            <CalculatorOutlined
              style={{ fontSize: '20px', color: '#437EF7' }}
              className="transition-transform duration-300 group-hover:scale-125"
            />
            Exposures
          </div>
        </Button>
        {isEnabled && (
          <Button
            type="menu"
            className={`group rounded-md py-2 px-3 sidebar-icon ${
              shouldBeHighlighted('/cyber') ? 'sidebar-icon--active' : ''
            }`}
            onClick={() => handleMenuClick('/cyber')}
          >
            <div className="w-full flex items-center gap-3">
              <AppstoreTwoTone
                style={{ fontSize: '20px', color: '#437EF7' }}
                className="transition-transform duration-300 group-hover:scale-125"
              />
              Cyber
            </div>
          </Button>
        )}
      </div>

      {(isAdmin || isSuperAdmin) && (
        <div>
          <h2 className="w-full text-xsm font-semibold text-grey tracking-wider">
            ACCESS
          </h2>
          <Button
            type="menu"
            className={`group rounded-md py-2 px-3 sidebar-icon ${
              shouldBeHighlighted('/users-management')
                ? 'sidebar-icon--active'
                : ''
            }`}
            onClick={() => handleMenuClick('/users-management')}
          >
            <div className="w-full flex items-center gap-3">
              <TeamOutlined
                style={{ fontSize: '20px', color: '#437EF7' }}
                className="transition-transform duration-300 group-hover:scale-125"
              />
              Users
            </div>
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <main className="w-full flex">
      {/* Hamburger Menu for small screens */}
      <div className="xl:hidden py-6 px-4">
        <Button type="primary" onClick={showDrawer}>
          <MenuOutlined />
        </Button>
      </div>

      {/* Sidebar for larger screens */}
      <div className="hidden xl:block w-56 h-screen p-6 border-r border-dashed sticky top-0 min-w-[224px]">
        {menuContent}
      </div>

      {/* Drawer for small screens */}
      <Drawer
        title="Menu"
        placement="left"
        onClose={closeDrawer}
        open={visible}
        className="xl:hidden"
      >
        {menuContent}
      </Drawer>

      {/* Main Content */}
      <div className="flex-1 w-full container">
        <div className="min-h-screen p-4 w-full">
          <Historybreadcrumb />
          <Outlet />
        </div>
      </div>
    </main>
  );
}

export default SideMenu;
